import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { downloadArtifact, fetchArtifactText } from '../api';
import type { ArtifactName } from '../types';

interface Props {
  token: string;
  jobId: string;
  artifact: ArtifactName;
  title: string;
  onClose: () => void;
}

export function ArtifactViewer({ token, jobId, artifact, title, onClose }: Props) {
  const [text, setText] = useState<string>();
  const [error, setError] = useState<string>();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    setText(undefined);
    setError(undefined);
    fetchArtifactText(token, jobId, artifact)
      .then((value) => !cancelled && setText(value))
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
      });
    return () => {
      cancelled = true;
    };
  }, [token, jobId, artifact]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The text ultimately derives from a recording and a model, so it is never
  // trusted: Markdown is rendered, then sanitised before it reaches the DOM.
  const html = useMemo(() => {
    if (text === undefined || artifact === 'todos-json') return undefined;
    return DOMPurify.sanitize(marked.parse(text, { async: false }));
  }, [text, artifact]);

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="viewer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="viewer-head">
          <strong>{title}</strong>
          <div className="viewer-actions">
            <button onClick={() => void downloadArtifact(token, jobId, artifact)}>
              Herunterladen
            </button>
            <button ref={closeRef} onClick={onClose} aria-label="Schließen">
              Schließen
            </button>
          </div>
        </header>

        <div className="viewer-body">
          {error && <p className="error">{error}</p>}
          {!error && text === undefined && <p className="muted">Wird geladen …</p>}
          {artifact === 'todos-json' && text !== undefined && <pre className="json">{text}</pre>}
          {html !== undefined && (
            <article className="markdown" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    </div>
  );
}
